import {
  Box,
  CircularProgress,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Button, ModalContentContainer } from '@/components';
import { urls } from '@/constants';
import { AnalyticsService, EVENTS } from '@/services';
import { setTokens, useAppDispatch, useVerifyEmailMutation } from '@/store';
import { SnackbarUtils } from '@/utils';

import { PageContainer } from '../components';

export const VerifyEmailPage = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { token } = Object.fromEntries(
    new URLSearchParams(location.search).entries(),
  );
  const [submit, { isLoading, isError, isSuccess, data }] =
    useVerifyEmailMutation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  useEffect(() => {
    if (token) {
      const verifyEmail = async () => {
        try {
          AnalyticsService.logEvent(
            EVENTS.AUTH_EVENTS.EMAIL_VALIDATION.INTENT,
            {
              type: 'primary email',
            },
          );
          const {
            response: { accessToken, refreshToken },
          } = await submit({ token }).unwrap();
          AnalyticsService.logEvent(
            EVENTS.AUTH_EVENTS.EMAIL_VALIDATION.SUCCESS,
            {
              type: 'primary email',
            },
          );
          if (!isMobile) {
            await dispatch(setTokens({ accessToken, refreshToken }));
          }
        } catch {
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.EMAIL_VALIDATION.FAIL, {
            type: 'primary email',
          });
        }
      };
      verifyEmail();
    }
  }, [token, isMobile, submit, dispatch]);

  return (
    <PageContainer hideCertificationInfo>
      <ModalContentContainer>
        {isLoading && (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <>
            <Typography variant="h3">Sorry, this link has expired</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              This email confirmation link is invalid, or has expired.
            </Typography>
          </>
        )}
        {isSuccess && isMobile && (
          <>
            <Typography variant="h3">Email Confirmed!</Typography>
            <Button
              variant="secondary"
              label="Stay in browser"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => {
                const { accessToken, refreshToken } = data?.response || {};
                if (!accessToken || !refreshToken) {
                  SnackbarUtils.error(
                    'Something went wrong. Please try again later.',
                  );
                  return;
                }
                dispatch(setTokens({ accessToken, refreshToken }));
              }}
            />
            <Button
              variant="primary"
              label="Continue to LumiQ"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => {
                window.open(
                  `${urls.auth.mobileRedirect}?magicLinkToken=${data?.response?.magicLinkToken}`,
                );
              }}
            />
          </>
        )}
      </ModalContentContainer>
    </PageContainer>
  );
};
